import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, CalendarDays, FolderUp, Megaphone, MessagesSquare, User, Settings, LogOut, ArrowLeft, PanelLeftClose, PanelLeftOpen, ChevronsUpDown, KeyRound, Activity, BookOpen } from 'lucide-react';
import teacherLogo from '../../assets/images/duotutor_teacher_logo.svg';
import { Popover, Menu, MenuItem, Tooltip } from '@bnvsrnd/brand-assets';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../utils/api';
import InstructorNoticeModal from './InstructorNoticeModal';
import { SupervisionChatProvider } from './SupervisionChat';
import './InstructorLayout.css';

/**
 * 멘토 포털 공통 셸 (좌측 사이드바, 헤더 없음 — 어드민 포털과 동일한 구조)
 * - 헤더 제거: 회원 메뉴(계정 관리·로그아웃)는 사이드바 하단 프로필로 이동
 * - 운영자(admin)는 사이드바 하단 "관리자" 링크(구분선 위)로 어드민 포털로 복귀
 * - 메인 사이트 상단바는 표시하지 않음(App.js 에서 /instructor 를 standalone 처리)
 */
const NAV_ITEMS = [
    { to: '/instructor/dashboard', label: '대시보드', icon: <LayoutDashboard size="1em" /> },
    { to: '/instructor/classes', label: '내 반', icon: <Users size="1em" /> },
    { to: '/instructor/calendar', label: '캘린더', icon: <CalendarDays size="1em" /> },
    { to: '/instructor/board', label: '게시판', icon: <MessagesSquare size="1em" /> },
    { to: '/instructor/league-wiki', label: '리그 위키', icon: <BookOpen size="1em" /> },
    { to: '/instructor/documents', label: '서류 제출', icon: <FolderUp size="1em" /> },
    { to: '/instructor/recruitment', label: '모집공고', icon: <Megaphone size="1em" /> },
];

function InstructorLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useContext(AuthContext);
    // 운영자(admin)는 메인 상단바를 쓰므로 멘토 포털 자체 상단바를 숨겨 하나로 합친다.
    const isAdmin = !!(user && (user.isAdmin || user.role === 'admin'));

    const [mobileOpen, setMobileOpen] = useState(false);
    // 슈퍼바이저 메뉴 — 수업 일정에 슈퍼바이저로 이름이 적힌 사람(스텝·강사)에게만 보인다(서버가 다시 확인).
    const [isSupervisor, setIsSupervisor] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get('/supervision/me')
            .then((res) => { if (alive) setIsSupervisor(!!res.data.isSupervisor); })
            .catch(() => { /* 메뉴 하나 — 실패하면 숨긴다 */ });
        return () => { alive = false; };
    }, []);
    const navItems = isSupervisor
        ? [...NAV_ITEMS, { to: '/instructor/supervision', label: '슈퍼바이저', icon: <Activity size="1em" /> }]
        : NAV_ITEMS;
    // 접힘 상태 — 페이지 이동/새로고침에도 유지(localStorage). 어드민·멘토 공통 키.
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
    });
    const toggleCollapsed = () => setCollapsed((v) => {
        const next = !v;
        try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
        return next;
    });

    // 활성 메뉴 판별: 정확히 일치하거나 하위 경로(예: /instructor/classes/:id)인 경우 강조
    const isActive = (to) => {
        if (location.pathname === to) return true;
        if (to === '/instructor/classes') {
            return location.pathname.startsWith('/instructor/classes');
        }
        return location.pathname.startsWith(`${to}/`);
    };

    // 콘텐츠 폭 캡(--app-content-width, 1280)을 해제하고 메인 영역 전체를 쓰는 페이지.
    // 결과물 칸반보드처럼 가로 공간이 중요한 화면만 예외 처리한다.
    // (사이드바는 .instructor-main 밖의 flex 형제라 아무리 넓혀도 침범되지 않는다.)
    const isFullWidthPage = /\/classes\/[^/]+\/deliverables$/.test(location.pathname);

    const closeMobile = () => setMobileOpen(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const displayName = (user && user.name) || '회원';
    const roleLabel = isAdmin ? '관리자' : '멘토';

    // 관리자 복귀 링크 — 운영자만, 구분선 위. 접힘 상태에선 DS Tooltip(우측)으로 감싼다.
    const adminLink = (
        <Link to="/admin/overview" className="instructor-nav-link" onClick={closeMobile}>
            <span className="instructor-nav-icon"><ArrowLeft size="1em" /></span>
            <span>관리자</span>
        </Link>
    );

    // 사이드바 하단 프로필 — 오른쪽으로 열리는 드롭다운(계정 관리 · MCP · 로그아웃).
    // @bnvsrnd/brand-assets의 Popover는 열려있는 동안 스크롤을 감지해 위치를 다시
    // 계산하지 않는다(패널을 처음 열 때 좌표를 한 번만 document 기준으로 계산) — 트리거가
    // 스크롤에 따라 화면상 위치가 바뀌면(사이드바가 position: sticky) 패널이 트리거를
    // 따라오지 못하고 스크롤한 만큼 어긋나 보인다. 완전한 해결은 라이브러리가 스크롤마다
    // 재계산해야 하지만, 우리 쪽에서는 스크롤이 나면 패널을 닫아서 "어긋난 채로 떠있는"
    // 상태 자체를 없앤다(controlled open으로 전환).
    //
    // 또한 placement="right"는 라이브러리가 트리거 세로 중앙 기준으로만 좌표를 잡고
    // 뷰포트 하단 클램프가 없어서(left/right 배치 공통 버그), 사이드바 맨 아래에 있는 이
    // 트리거에서는 패널 아래쪽이 살짝 잘렸다. .profile-popover-panel 클래스로 position을
    // fixed로 강제하고 bottom을 고정값으로 줘서, 트리거 위치와 무관하게 항상 화면 하단에서
    // 일정 간격 띄운 채로 표시되게 한다(InstructorLayout.css 참조).
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    useEffect(() => {
        if (!profileMenuOpen) return undefined;
        const handleScroll = () => setProfileMenuOpen(false);
        // capture: true — 사이드바 자신의 overflow-y:auto 스크롤처럼 window까지 버블링되지
        // 않는 스크롤 이벤트도 캡처링 단계에서는 잡힌다.
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [profileMenuOpen]);

    const profilePopover = (
        <Popover
            placement="right"
            open={profileMenuOpen}
            onOpenChange={setProfileMenuOpen}
            className="profile-popover-panel"
            trigger={
                <button type="button" className="instructor-profile">
                    <span className="instructor-profile-avatar"><User size={18} /></span>
                    <span className="instructor-profile-info">
                        <span className="instructor-profile-name">{displayName}</span>
                        <span className="instructor-profile-role">{roleLabel}</span>
                    </span>
                    <ChevronsUpDown size={16} className="instructor-profile-chevron" />
                </button>
            }
        >
            <Menu>
                <MenuItem icon={<Settings size={16} />} onClick={() => navigate('/instructor/account')}>
                    계정 관리
                </MenuItem>
                <MenuItem icon={<KeyRound size={16} />} onClick={() => navigate('/instructor/mcp-tokens')}>
                    MCP
                </MenuItem>
                <MenuItem type="destructive" icon={<LogOut size={16} />} onClick={handleLogout}>
                    로그아웃
                </MenuItem>
            </Menu>
        </Popover>
    );

    return (
        <SupervisionChatProvider>
        <div className="instructor-shell">
            {/* 모바일 사이드바 백드롭 */}
            <div
                className={`instructor-sidebar-backdrop ${mobileOpen ? 'open' : ''}`}
                onClick={closeMobile}
            />

            {/* 사이드바 — 상단부터 하단까지 풀하이트. 브랜드 로고를 상단에 포함 */}
            <nav className={`instructor-sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
                <div className="instructor-sidebar-brand">
                    <Link
                        to="/instructor/dashboard"
                        className="instructor-sidebar-logo"
                        onClick={closeMobile}
                    >
                        <img src={teacherLogo} alt="DuoTutor 멘토" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                    <button
                        type="button"
                        className="instructor-collapse-toggle"
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
                    >
                        {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                    </button>
                </div>

                <div className="instructor-sidebar-nav">
                    <div className="instructor-nav-section-label">메뉴</div>
                    {navItems.map((item) => {
                        const link = (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`instructor-nav-link ${isActive(item.to) ? 'active' : ''}`}
                                onClick={closeMobile}
                            >
                                <span className="instructor-nav-icon">{item.icon}</span>
                                <span>{item.label}</span>
                            </Link>
                        );
                        // 접힘 상태: 라벨이 안 보이므로 DS Tooltip(우측)으로 보완
                        return collapsed
                            ? <Tooltip key={item.to} content={item.label} position="right">{link}</Tooltip>
                            : link;
                    })}
                </div>

                {/* 관리자 복귀 — 구분선 위 (운영자만) */}
                {isAdmin && (
                    <div className="instructor-sidebar-switch">
                        {collapsed
                            ? <Tooltip content="관리자" position="right">{adminLink}</Tooltip>
                            : adminLink}
                    </div>
                )}

                {/* 하단 — 프로필 (구분선 아래, 오른쪽으로 열리는 드롭다운) */}
                <div className="instructor-sidebar-foot">
                    {profilePopover}
                </div>
            </nav>

            {/* 메인 컬럼 (헤더 없음 — 콘텐츠만) */}
            <div className="instructor-main">
                {/* 모바일 전용: 사이드바 토글 (헤더가 없어 플로팅 버튼으로 제공) */}
                <button
                    type="button"
                    className="instructor-mobile-toggle"
                    aria-label="메뉴 열기"
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    ☰
                </button>

                {/* 콘텐츠 */}
                <main className="instructor-content">
                    <div className={`instructor-content-inner${isFullWidthPage ? ' instructor-content-inner--full' : ''}`}>
                        {children}
                        <InstructorNoticeModal />
                    </div>
                </main>
            </div>
        </div>
        </SupervisionChatProvider>
    );
}

export default InstructorLayout;
